import React from 'react';
import { motion } from 'framer-motion';
import { Scissors, Sparkles, Droplets, Crown, Palette, Wind } from 'lucide-react';
import { useLanguage } from './LanguageContext';
interface ServiceItem {
  nameKey: string;
  price: string;
}
interface ServiceCategory {
  titleKey: string;
  icon: React.ElementType;
  items: ServiceItem[];
}
const categories: ServiceCategory[] = [{
  titleKey: 'services.cat.hairCuts',
  icon: Scissors,
  items: [{
    nameKey: 'services.item.women',
    price: 'from $45'
  }, {
    nameKey: 'services.item.men',
    price: 'from $45'
  }]
}, {
  titleKey: 'services.cat.styling',
  icon: Wind,
  items: [{
    nameKey: 'services.item.blowDryShort',
    price: 'from $35'
  }, {
    nameKey: 'services.item.blowDryMedium',
    price: 'from $50'
  }, {
    nameKey: 'services.item.blowDryLong',
    price: 'from $70'
  }, {
    nameKey: 'services.item.flatCurlyIron',
    price: 'from $15'
  }]
}, {
  titleKey: 'services.cat.highlights',
  icon: Sparkles,
  items: [{
    nameKey: 'services.item.fullFoilShort',
    price: 'from $110'
  }, {
    nameKey: 'services.item.fullFoilMedium',
    price: 'from $150'
  }, {
    nameKey: 'services.item.fullFoilLong',
    price: 'from $200'
  }, {
    nameKey: 'services.item.partialFoil',
    price: 'from $90'
  }]
}, {
  titleKey: 'services.cat.treatments',
  icon: Droplets,
  items: [{
    nameKey: 'services.item.keratin',
    price: 'from $150'
  }, {
    nameKey: 'services.item.botox',
    price: 'from $130'
  }, {
    nameKey: 'services.item.hydrate',
    price: 'from $35'
  }, {
    nameKey: 'services.item.olaplex',
    price: 'from $45'
  }]
}, {
  titleKey: 'services.cat.color',
  icon: Palette,
  items: [{
    nameKey: 'services.item.colorRoots',
    price: 'from $55'
  }, {
    nameKey: 'services.item.fullColorShort',
    price: 'from $70'
  }, {
    nameKey: 'services.item.fullColorMedium',
    price: 'from $90'
  }, {
    nameKey: 'services.item.fullColorLong',
    price: 'from $150'
  }, {
    nameKey: 'services.item.balayageColor',
    price: 'from $200'
  }]
}, {
  titleKey: 'services.cat.hairExtensions',
  icon: Crown,
  items: [{
    nameKey: 'services.item.fusionHair',
    price: 'from $750'
  }, {
    nameKey: 'services.item.tapeHair',
    price: 'from $550'
  }, {
    nameKey: 'services.item.clipHair',
    price: 'from $500'
  }, {
    nameKey: 'services.item.volume',
    price: 'from $350'
  }]
}];
export function Services() {
  const {
    t,
    language
  } = useLanguage();
  const isRtl = language === 'he';
  return <section id="services" className="py-24 bg-[#0f0f0f] relative border-t border-studio-gold/10" data-id="element-98">
      <div className="max-w-6xl mx-auto px-4" data-id="element-99">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-100">
          <h2 className="text-3xl md:text-5xl font-playfair text-white mb-4" data-id="element-101">
            {t('services.title')}
          </h2>
          <div className="w-24 h-[1px] bg-studio-gold mx-auto mb-6" data-id="element-102"></div>
          <p className="text-gray-400 font-inter font-light max-w-xl mx-auto" data-id="element-103">
            {t('services.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-104">
          {categories.map((category, catIndex) => <motion.div key={catIndex} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: catIndex * 0.1
        }} className="border border-studio-gold/10 bg-studio-black p-8 hover:border-studio-gold/30 transition-all duration-300" data-id="element-105">
              <div className="flex items-center gap-3 mb-6" data-id="element-106">
                <category.icon className="w-6 h-6 text-studio-gold shrink-0" strokeWidth={1.5} data-id="element-107" />
                <h3 className="text-xl font-playfair text-studio-gold" data-id="element-108">
                  {t(category.titleKey)}
                </h3>
              </div>

              <div className="space-y-0" data-id="element-109">
                {category.items.map((item, itemIndex) => <div key={itemIndex} className="flex items-center justify-between py-3 border-b border-white/5 last:border-b-0" data-id="element-110">
                    <span className="text-gray-300 font-inter font-light text-sm" data-id="element-111">
                      {t(item.nameKey)}
                    </span>
                    <span className={`text-studio-gold font-inter text-sm font-medium whitespace-nowrap ${isRtl ? 'mr-4' : 'ml-4'}`} data-id="element-112">
                      {item.price}
                    </span>
                  </div>)}
              </div>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="text-center mt-16" data-id="element-113">
          <a href="https://wa.me/17863086806" target="_blank" rel="noopener noreferrer" className="inline-block bg-studio-gold hover:bg-studio-gold-light text-studio-black font-inter font-semibold px-8 py-4 rounded-none tracking-widest uppercase transition-all duration-300 hover:shadow-[0_0_30px_rgba(201,168,76,0.4)]" data-id="element-114">
            {t('services.bookBtn')}
          </a>
        </motion.div>
      </div>
    </section>;
}